/* app/onboarding/steps.jsx — сценарий обязательного игрового онбординга («квест»).
   Декларативный список шагов; логику (навигация, завершение, конфетти) держит
   OnboardingOverlay. Тур ОБЯЗАТЕЛЬНЫЙ — кнопки «пропустить» нет.

   Виды шага:
   - kind:'panel'     — центрированная карточка (welcome/celebrate/business/finish).
   - kind:'spotlight' — подсветка реального элемента (target) + подсказка-пузырь.

   Условие перехода (advance) у spotlight-шага:
   - {type:'button', label}   — кнопка в подсказке;
   - {type:'event',  name}    — window CustomEvent (onb:sent / onb:committed);
   - {type:'appear', selector}— селектор появился в DOM (результат ИИ);
   - {type:'goto',   match}   — пользователь сам перешёл: URL (pathname+search)
                                содержит match. У таких шагов clickThrough:true —
                                затемнение НЕ блокирует клик, чтобы можно было нажать
                                подсвеченный пункт меню/вкладку.

   Привязка к разделу тарифа (tourSteps.js, lib/planSections.js):
   - section:'analytics'       — шаг пропускается, если тариф пространства закрыл раздел
                                 (workspace.sections.analytics === false);
   - copyWithout:{analytics:{…}} — поля, которые подменяют текст шага при закрытом разделе. */

// Курированный пример: блюдо с полуфабрикатом (тунец конфи) — нейросеть сама соберёт
// и ТТК блюда, и отдельный п/ф, и карточки сырья (демонстрация авто-сборки дерева).
export const DEMO_TTK_PROMPT =
  'Сделай ТТК на салат Нисуаз с тунцом конфи для ресторана формата casual dining. ' +
  'Выход салата 260гр.';

// Бургер, открывающий навигацию на узких раскладках (сайдбар свёрнут):
// ChatHeader (на /chat) и PageHeader (в разделах).
const NAV_HAMBURGER = 'button[aria-label="Меню и диалоги"], .page-header__menu';

export const STEPS = [
  { id: 'welcome', kind: 'panel', panel: 'welcome' },

  {
    id: 'orient', kind: 'spotlight', target: '.sidebar__nav, .tab-bar',
    fallbackTarget: NAV_HAMBURGER, placement: 'auto',
    title: 'Ваше рабочее пространство',
    body: 'Четыре раздела: Ассистент, Продукция, Аналитика и Отчёты. Пройдём по ним — но сначала ассистент соберёт вашу первую техкарту.',
    copyWithout: {
      analytics: { body: 'Три раздела: Ассистент, Продукция и Отчёты. Пройдём по ним — но сначала ассистент соберёт вашу первую техкарту.' },
    },
    advance: { type: 'button', label: 'Дальше' },
  },
  {
    id: 'send', kind: 'spotlight', target: '.composer__send', fallbackTarget: '.composer',
    pad: 8, placement: 'top',
    collapseSidebar: true,   // фокус на чате: сворачиваем постоянный сайдбар на время сборки ТТК
    title: 'Нейросеть в деле',
    body: 'Мы уже вписали пример — техкарту салата. Нажмите кнопку «Отправить» (текст можно изменить) и смотрите, как ассистент её собирает.',
    hint: 'Нажмите «Отправить» →',
    advance: { type: 'event', name: 'onb:sent' },
  },
  {
    id: 'working', kind: 'spotlight', target: '.chat-conv__body', pad: 6, placement: 'left',
    collapseSidebar: true,
    title: 'Считает прямо сейчас',
    body: 'Нейросеть разбирает состав, рассчитывает КБЖУ, выход, себестоимость и фудкост. Несколько секунд.',
    hint: 'Нейросеть думает…',
    timed: true,            // показывать счётчик секунд в подсказке
    resendAfterMs: 30000,   // через 30с — «Долго отвечает? Отправить ещё раз» (10–15с — норма, раньше не пугаем)
    emergencyAfterMs: 60000, // через 60с (явный сбой LLM, после шанса на повтор) — мягкий «Продолжить»
    advance: { type: 'appear', selector: '[data-onb="draft-commit"]' },
  },
  {
    id: 'draft', kind: 'spotlight', target: '.msg--assistant:has([data-onb="draft-card"])',
    fallbackTarget: '[data-onb="draft-card"]', pad: 10, placement: 'left',
    collapseSidebar: true,
    title: 'Готовый черновик',
    body: 'Цифры посчитал код, а не выдумала модель. Любую граммовку можно поправить прямо здесь — пересчёт мгновенный.',
    advance: { type: 'button', label: 'Дальше' },
  },
  {
    id: 'commit', kind: 'spotlight', target: '[data-onb="draft-commit"]', pad: 8, placement: 'top',
    collapseSidebar: true,
    title: 'Сделайте её своей',
    body: 'Нажмите «Утвердить и добавить» — техкарта сохранится в разделе «Продукция».',
    hint: 'Нажмите «Утвердить и добавить»',
    advance: { type: 'event', name: 'onb:committed' },
  },

  { id: 'celebrate', kind: 'panel', panel: 'celebrate' },

  // ── Продукция (пользователь сам жмёт пункт меню) ──
  {
    id: 'go-kitchen', kind: 'spotlight', target: '[data-onb="nav-kitchen"]', pad: 6, placement: 'auto',
    clickThrough: true, fallbackTarget: NAV_HAMBURGER, fallbackHint: 'Откройте меню, затем «Продукция»',
    title: 'Где живёт техкарта',
    body: 'Откройте меню (если свёрнуто) и нажмите «Продукция» — посмотрим, куда попала ваша ТТК.',
    hint: 'Нажмите «Продукция»',
    advance: { type: 'goto', match: '/kitchen' },
  },
  {
    id: 'kitchen-sirye', kind: 'spotlight', target: '.section-body', pad: 8, placement: 'auto',
    title: 'Сырьё завелось само',
    body: 'Это раздел «Продукция» — ядро системы: Сырьё, Полуфабрикаты, Готовые позиции и Проекты меню. Нейросеть уже завела сюда карточки сырья с КБЖУ и категориями — вручную ничего вводить не нужно.',
    advance: { type: 'button', label: 'А полуфабрикаты?' },
  },
  {
    id: 'kitchen-pf', kind: 'spotlight', target: '[data-onb="tab-semiproducts"]', pad: 6, placement: 'bottom',
    clickThrough: true, fallbackTarget: '.section-body',
    title: 'И полуфабрикаты — тоже',
    body: 'Откройте «Полуфабрикаты»: сложную заготовку (тунец конфи) нейросеть собрала отдельным п/ф — со своим составом и себестоимостью. Все п/ф и ингредиенты создались автоматически.',
    hint: 'Нажмите «Полуфабрикаты»',
    advance: { type: 'goto', match: 'tab=semiproducts' },
  },
  {
    id: 'kitchen-menu', kind: 'spotlight', target: '[data-onb="tab-menus"]', pad: 6, placement: 'bottom',
    clickThrough: true, fallbackTarget: '.section-body',
    title: 'А ещё — меню',
    body: 'Из готовых блюд во вкладке «Проекты меню» собирается меню заведения: наценка, фудкост, печать. Создадите своё, когда будете готовы.',
    advance: { type: 'button', label: 'Где моё блюдо?' },
  },
  {
    id: 'kitchen-blyuda', kind: 'spotlight', target: '[data-onb="tab-recipes"]', pad: 6, placement: 'bottom',
    clickThrough: true, fallbackTarget: '.section-body',
    title: 'Ваши готовые позиции',
    body: 'Откройте вкладку «Готовые позиции» — там лежит готовая техкарта.',
    hint: 'Нажмите «Готовые позиции»',
    advance: { type: 'goto', match: 'tab=recipes' },
  },
  {
    id: 'kitchen-ttk', kind: 'spotlight', target: '.tile--recipe[aria-label="{dish}"]', pad: 8, placement: 'auto',
    clickThrough: true, fallbackTarget: '.tile--recipe',
    title: 'А вот она!',
    body: '«{dish}» — техкарта, которую собрала нейросеть: состав, себестоимость и фудкост уже посчитаны. Нажмите на карточку, чтобы открыть её.',
    hint: 'Откройте карточку блюда',
    advance: { type: 'goto', match: 'edit=ttk' },
  },
  {
    id: 'kitchen-edit', kind: 'spotlight', target: '.drawer[role="dialog"]', pad: 0, placement: 'left',
    clickThrough: true, fallbackTarget: '.section-body',
    title: 'Всё можно корректировать',
    body: 'Любую граммовку, состав, технологию или цену можно поменять прямо тут — пересчёт мгновенный. Закройте карточку, чтобы продолжить.',
    hint: 'Закройте карточку',
    advance: { type: 'goto', match: 'edit=ttk', gone: true },
  },

  // ── Аналитика ──
  {
    id: 'go-analytics', section: 'analytics', kind: 'spotlight', target: '[data-onb="nav-analytics"]', pad: 6, placement: 'auto',
    clickThrough: true, fallbackTarget: NAV_HAMBURGER, fallbackHint: 'Откройте меню, затем «Аналитика»',
    title: 'Аналитика',
    body: 'Откройте меню (если свёрнуто) и нажмите «Аналитика» — покажу сводку по вашей продукции.',
    hint: 'Нажмите «Аналитика»',
    advance: { type: 'goto', match: '/analytics' },
  },
  {
    id: 'analytics', section: 'analytics', kind: 'spotlight', target: '.section-body', pad: 8, placement: 'auto',
    title: 'Раздел «Аналитика»',
    body: 'Дашборд по деньгам: самые дорогие блюда, тренды закупочных цен и всплески — чтобы держать себестоимость под контролем.',
    advance: { type: 'button', label: 'Дальше' },
  },

  // ── Отчёты ──
  {
    id: 'go-reports', kind: 'spotlight', target: '[data-onb="nav-reports"]', pad: 6, placement: 'auto',
    clickThrough: true, fallbackTarget: NAV_HAMBURGER, fallbackHint: 'Откройте меню, затем «Отчёты»',
    title: 'Отчёты',
    body: 'Откройте меню (если свёрнуто) и нажмите «Отчёты» — последний раздел.',
    hint: 'Нажмите «Отчёты»',
    advance: { type: 'goto', match: '/reports' },
  },
  {
    id: 'reports', kind: 'spotlight', target: '.section-body', pad: 8, placement: 'auto',
    title: 'Раздел «Отчёты»',
    body: 'Сюда попадают готовые AI-разборы: оптимизация фудкоста, ABC-анализ меню и инвентаризации — с выгрузкой в файл.',
    advance: { type: 'button', label: 'И последнее' },
  },

  // ── Финал: бизнес-пространство + поздравление ──
  { id: 'business', kind: 'panel', panel: 'business' },
  {
    id: 'finish', kind: 'panel', panel: 'finish',
    // Текст финальной карточки живёт здесь (раньше был зашит в OnboardingOverlay), чтобы
    // его можно было подменить для пространства без аналитики.
    lead: 'Собрали техкарту руками нейросети и прошли по Продукции, Аналитике и Отчётам. Дальше — за вами.',
    copyWithout: {
      analytics: { lead: 'Собрали техкарту руками нейросети и прошли по Продукции и Отчётам. Дальше — за вами.' },
    },
  },
];
